import { type DragEvent, type FormEvent, type ReactNode, type Ref, useId, useState } from "react";
import { useTranslation } from "react-i18next";
import { COMPOSER_FRAME, SendButton } from "@/components/SendButton";
import { cn } from "@/lib/utils";

/** Incumbent lifted frame, shared by Home and both conversation entry points. */
export function ComposerFrame(props: {
  children: ReactNode;
  onSubmit: (e: FormEvent) => void;
  onFiles?: ((files: File[]) => void) | undefined;
  className?: string;
  describedBy?: string | undefined;
}) {
  const [dragging, setDragging] = useState(false);
  const filesOnly = (e: DragEvent) => [...e.dataTransfer.types].includes("Files");
  return (
    <form
      data-esi-content="composer"
      className={cn(
        "flex min-w-0 flex-col",
        COMPOSER_FRAME,
        dragging && "border-line-strong bg-surface-sunken",
        props.className,
      )}
      aria-describedby={props.describedBy}
      onSubmit={props.onSubmit}
      onDragOver={(e) => {
        if (props.onFiles && filesOnly(e)) {
          e.preventDefault();
          e.dataTransfer.dropEffect = "copy";
          setDragging(true);
        }
      }}
      onDragLeave={(e) => {
        if (!e.currentTarget.contains(e.relatedTarget as Node | null)) {
          setDragging(false);
        }
      }}
      onDrop={(e) => {
        setDragging(false);
        if (props.onFiles && filesOnly(e)) {
          e.preventDefault();
          props.onFiles([...e.dataTransfer.files]);
        }
      }}
    >
      {props.children}
    </form>
  );
}

export function ComposerField(props: {
  value: string;
  onChange: (text: string) => void;
  onSend: () => void;
  placeholder: string;
  label?: string;
  disabled?: boolean;
  describedBy?: string | undefined;
  inputRef?: Ref<HTMLTextAreaElement> | undefined;
}) {
  const id = useId();
  return (
    <>
      <label htmlFor={id} className="sr-only">
        {props.label ?? props.placeholder}
      </label>
      <textarea
        ref={props.inputRef}
        id={id}
        rows={1}
        value={props.value}
        disabled={props.disabled}
        placeholder={props.placeholder}
        aria-describedby={props.describedBy}
        onChange={(e) => props.onChange(e.currentTarget.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter" && !e.shiftKey && !e.nativeEvent.isComposing) {
            e.preventDefault();
            props.onSend();
          }
        }}
        className="field-sizing-content max-h-48 min-h-14 w-full min-w-0 resize-none bg-transparent px-5 pt-4 pb-1 text-[15px] leading-relaxed text-fg outline-none placeholder:text-fg-muted disabled:cursor-not-allowed"
      />
    </>
  );
}
export function ComposerFooter(props: {
  leading?: ReactNode;
  picker?: ReactNode;
  disabled: boolean;
  label?: string;
}) {
  const { t } = useTranslation();
  return (
    <div className="flex min-w-0 flex-wrap items-center justify-between gap-2 px-3 pt-1 pb-3">
      <div className="flex min-w-0 flex-1 items-center gap-1">{props.leading}</div>
      {/* Positioned after the leading controls, so it paints and takes clicks above anything that overflows them (#430). */}
      <div className="relative flex shrink-0 items-center gap-1">
        {props.picker}
        <SendButton label={props.label ?? t("conversation.send")} disabled={props.disabled} />
      </div>
    </div>
  );
}
